const STATUS_BAR_KEY = 'app_statusBarHeight';
const APP_FROM_KEY = 'app_from_channel';
const SHARE_BASE_KEY = 'app_shareBaseUrl';
const SHARE_COMPANY_KEY = 'app_shareCompanyId';
const SHARE_AGENT_KEY = 'app_shareAgentId';

/** 解析 App WebView 注入的 statusBarHeight / shareBaseUrl / 分享人信息并写入 sessionStorage */
export function initAppHostParams() {
  const params = new URLSearchParams(location.search);
  const statusBarHeight = params.get('statusBarHeight');
  if (statusBarHeight) sessionStorage.setItem(STATUS_BAR_KEY, statusBarHeight);
  const shareBaseUrl = params.get('shareBaseUrl');
  if (shareBaseUrl) sessionStorage.setItem(SHARE_BASE_KEY, shareBaseUrl.trim());
  if (params.get('from') === 'app') {
    sessionStorage.setItem(APP_FROM_KEY, '1');
  }
  const companyId = params.get('companyId');
  if (companyId && Number(companyId) > 0) {
    sessionStorage.setItem(SHARE_COMPANY_KEY, String(companyId).trim());
  }
  const agentId = params.get('agentId') || params.get('userId');
  if (agentId && Number(agentId) > 0) {
    sessionStorage.setItem(SHARE_AGENT_KEY, String(agentId).trim());
  }

  // App 打开业务 H5：同步 userId/companyId 到 userInfo，供请求头使用
  try {
    const userId = params.get('userId');
    if (userId && String(userId).trim()) {
      const raw = localStorage.getItem('userInfo');
      let info: Record<string, unknown> = {};
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') info = parsed;
        } catch {
          /* ignore */
        }
      }
      info.userId = String(userId).trim();
      if (companyId && Number(companyId) > 0) info.companyId = String(companyId).trim();
      if (params.get('agentId')) info.agentId = String(params.get('agentId')).trim();
      localStorage.setItem('userInfo', JSON.stringify(info));
    }
  } catch {
    /* ignore */
  }
}

export function getCachedShareCompanyId(): string {
  return sessionStorage.getItem(SHARE_COMPANY_KEY) || '';
}

export function getCachedShareAgentId(): string {
  return sessionStorage.getItem(SHARE_AGENT_KEY) || '';
}

export function cacheShareIdentity(companyId?: string | number | null, agentId?: string | number | null) {
  if (companyId != null && String(companyId).trim() && Number(companyId) > 0) {
    sessionStorage.setItem(SHARE_COMPANY_KEY, String(companyId).trim());
  }
  if (agentId != null && String(agentId).trim() && Number(agentId) > 0) {
    sessionStorage.setItem(SHARE_AGENT_KEY, String(agentId).trim());
  }
}

export function getAppStatusBarHeight(): number {
  const raw =
    new URLSearchParams(location.search).get('statusBarHeight') ||
    sessionStorage.getItem(STATUS_BAR_KEY);
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function trimTrailingSlash(url: string): string {
  return String(url || '').trim().replace(/\/+$/, '');
}

function isIpOrLocalHost(hostname: string): boolean {
  if (!hostname) return true;
  if (hostname === 'localhost' || hostname.endsWith('.local')) return true;
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
}

function isPublicWebHost(raw?: string | null): boolean {
  const text = trimTrailingSlash(String(raw || ''));
  if (!text) return false;
  try {
    const withProto = /^https?:\/\//i.test(text) ? text : `https://${text}`;
    const host = new URL(withProto).hostname.toLowerCase();
    return !!host && !isIpOrLocalHost(host);
  } catch {
    return false;
  }
}

/** 对外分享 H5 公网域名：App 注入 > 构建环境变量 > 当前页 origin */
export function resolvePublicH5BaseUrl(): string {
  const fromApp = sessionStorage.getItem(SHARE_BASE_KEY);
  if (fromApp && isPublicWebHost(fromApp)) return trimTrailingSlash(fromApp);

  const fromEnv = import.meta.env.VITE_PUBLIC_H5_URL;
  if (fromEnv && isPublicWebHost(fromEnv)) return trimTrailingSlash(fromEnv);

  const origin = window.location.origin;
  if (isPublicWebHost(origin)) return trimTrailingSlash(origin);

  return trimTrailingSlash(fromApp || fromEnv || origin);
}

/**
 * 公众号网页授权回调域名：必须与用户当前打开的 H5 主机名一致（否则微信 10003）。
 * 不可使用 App 注入的 shareBaseUrl（可能是生产域名或其它环境）。
 */
export function resolveMpOAuthBaseUrl(): string {
  const origin = window.location.origin;
  if (isPublicWebHost(origin)) return trimTrailingSlash(origin);

  const fromEnv = import.meta.env.VITE_PUBLIC_H5_URL;
  if (fromEnv && isPublicWebHost(fromEnv)) return trimTrailingSlash(fromEnv);

  return resolvePublicH5BaseUrl();
}

export function buildPublicH5Url(path: string, query?: Record<string, string>): string {
  const base = resolvePublicH5BaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(normalizedPath, `${base}/`);
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value != null && String(value).length > 0) {
        url.searchParams.set(key, String(value));
      }
    });
  }
  return url.href;
}

const APP_WEBVIEW_PREFIXES = [
  '/insurance-plan',
  '/financial-safety',
  '/product-compare'
];

export function shouldApplyAppStatusBarPadding(path: string): boolean {
  return APP_WEBVIEW_PREFIXES.some((prefix) => path.startsWith(prefix));
}


/** 同步状态栏高度 CSS 变量（业务模块可单独消费，不必再给 body 加内边距） */
export function syncAppStatusBarCssVar() {
  const h = getAppStatusBarHeight();
  document.documentElement.style.setProperty(
    '--app-status-bar-height',
    h ? `${h}px` : '0px'
  );
}

export function applyAppStatusBarPadding() {
  syncAppStatusBarCssVar();
  const h = getAppStatusBarHeight();
  document.body.style.paddingTop = h ? `${h}px` : '';
}

export function clearAppStatusBarPadding() {
  document.body.style.paddingTop = '';
  document.documentElement.style.setProperty('--app-status-bar-height', '0px');
  document.documentElement.classList.remove('tc-app-host');
}

/**
 * 按路由应用 App WebView 宿主样式。
 */
export function applyRouteAppHost(path: string) {
  syncAppStatusBarCssVar();
  document.documentElement.classList.remove('tc-app-host');

  if (shouldApplyAppStatusBarPadding(path)) {
    const h = getAppStatusBarHeight();
    document.body.style.paddingTop = h ? `${h}px` : '';
  } else {
    document.body.style.paddingTop = '';
    document.documentElement.style.setProperty('--app-status-bar-height', '0px');
  }
}

/**
 * 是否在微信小程序 web-view 内。
 * 注意：引入 jweixin 后 App WebView 里也可能有 window.wx.miniProgram，
 * 不能仅凭 wx.miniProgram 判断，否则会把 App 误判成小程序导致不走 __app_bridge__。
 */
/** App 展业壳打开（from=app） */
export function isFromAppChannel(): boolean {
  try {
    if (new URLSearchParams(location.search).get('from') === 'app') return true;
    return sessionStorage.getItem(APP_FROM_KEY) === '1';
  } catch {
    return false;
  }
}

export function isWechatMiniProgramWebView(): boolean {
  try {
    const w = window as any;
    if (w.__wxjs_environment === 'miniprogram') return true;
  } catch {
    /* ignore */
  }
  // 小程序 web-view UA 含 miniProgram；App / 普通浏览器没有
  return /miniProgram/i.test(navigator.userAgent || '');
}

/**
 * App WebView：hideHeader 后由 H5 自绘标题栏。
 * 小程序 web-view 自带导航栏（读 document.title），再画 H5 标题会双标题。
 */
export function shouldShowH5AppNavBar(): boolean {
  return !isWechatMiniProgramWebView();
}
